/* Enhancements for the Vario colour-swatch grid:
   1) Vario uses 50px source images for swatches (blurry on high-DPI);
      upgrade inline backgrounds to the 120px rendition.
   2) Insert a type-to-filter field above large grids so customers who
      know the colour NAME (or number) can narrow the swatches.
   3) Badge swatches whose colour is on sale (any variant of that colour
      with compare-at > price), from the /products/<handle>.js endpoint.
   4) Give each colour swatch a real <a href="?variant=…"> overlay so the
      colour URLs are crawlable (VARIANT-SEO). Plain clicks are forwarded
      to Vario unchanged; middle/ctrl-click opens the colour URL.
   childList-only observer; our own insertions are guarded so the extra
   observer cycle they trigger settles immediately. */
(() => {
  const MIN_SWATCHES_FOR_FILTER = 12;
  let lastQuery = '';
  let saleColours = null; // Set of lowercased colour values on sale; null until loaded
  let salePct = new Map(); // colour -> dypeste rabattprosent (for badgen)
  let allColours = null; // every value of the colour option, to identify its grid
  let colourVariantIds = null; // lowercased colour value -> variant id (first match)
  let productHandle = null;
  let saleFetchStarted = false;

  const upgradeImages = () => {
    document.querySelectorAll('.vario-variant').forEach((el) => {
      [el, ...el.querySelectorAll('[style]')].forEach((t) => {
        const bg = t.style && t.style.backgroundImage;
        if (bg && bg.includes('_50x')) {
          t.style.backgroundImage = bg.replaceAll('_50x', '_120x');
        }
      });
    });
  };

  const itemsOf = (wrap) =>
    [...wrap.querySelectorAll('li, .vario-variant')].filter(
      (el) => el.querySelector('[data-value]') || el.hasAttribute('data-value')
    );

  const applyFilter = (wrap, query) => {
    const q = query.trim().toLowerCase();
    itemsOf(wrap).forEach((el) => {
      const holder = el.hasAttribute('data-value') ? el : el.querySelector('[data-value]');
      const name = (holder.getAttribute('data-value') || '').toLowerCase();
      el.style.display = !q || name.includes(q) ? '' : 'none';
    });
  };

  const ensureFilter = () => {
    document.querySelectorAll('.vario-variants').forEach((wrap) => {
      if (itemsOf(wrap).length < MIN_SWATCHES_FOR_FILTER) return;
      let input = wrap.parentElement.querySelector('.vario-name-filter');
      if (!input) {
        input = document.createElement('input');
        input.type = 'search';
        input.className = 'vario-name-filter';
        input.placeholder = 'Søk fargenavn …';
        input.setAttribute('aria-label', 'Filtrer farger etter navn');
        input.addEventListener('input', () => {
          lastQuery = input.value;
          applyFilter(wrap, lastQuery);
        });
        wrap.parentElement.insertBefore(input, wrap);
      }
      if (lastQuery && input.value !== lastQuery) {
        input.value = lastQuery;
      }
      applyFilter(wrap, lastQuery);
    });
  };

  const loadSaleColours = () => {
    if (saleFetchStarted) return;
    saleFetchStarted = true;
    const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) {
      saleColours = new Set();
      return;
    }
    productHandle = m[1];
    fetch(`/products/${m[1]}.js`)
      .then((r) => (r.ok ? r.json() : null))
      .then((product) => {
        saleColours = new Set();
        allColours = new Set();
        colourVariantIds = new Map();
        if (!product) return;
        // Only the option actually NAMED colour counts — sizes etc. must
        // never badge. options come as strings (.js endpoint) but tolerate
        // {name} objects too.
        const idx = (product.options || []).findIndex((o) =>
          /farge|colou?r/i.test(typeof o === 'string' ? o : o && o.name)
        );
        if (idx < 0) return;
        (product.variants || []).forEach((v) => {
          const val = v[`option${idx + 1}`];
          if (!val) return;
          const key = val.toLowerCase();
          allColours.add(key);
          if (!colourVariantIds.has(key)) colourVariantIds.set(key, v.id);
          if (v.compare_at_price && v.compare_at_price > v.price) {
            saleColours.add(key);
            const pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
            if (pct > (salePct.get(key) || 0)) salePct.set(key, pct);
          }
        });
        if (allColours.size) schedule();
      })
      .catch(() => {
        saleColours = new Set();
        allColours = new Set();
        colourVariantIds = new Map();
        salePct = new Map();
      });
  };

  // Crawlable colour link laid over the swatch. Plain left-clicks are
  // handed back to Vario (unchanged UX); modified clicks/middle-click get
  // normal link behaviour.
  const addVariantLink = (el, holder, name) => {
    if (el.querySelector('a.vario-variant-link')) return;
    const id = colourVariantIds.get(name);
    if (!id || !productHandle) return;
    const a = document.createElement('a');
    a.className = 'vario-variant-link';
    a.href = `/products/${productHandle}?variant=${id}`;
    a.setAttribute('aria-hidden', 'true');
    a.setAttribute('tabindex', '-1');
    a.style.cssText = 'position:absolute;inset:0;z-index:1;';
    a.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      holder.click();
    });
    const st = getComputedStyle(el);
    if (st.position === 'static') el.style.position = 'relative';
    el.appendChild(a);
  };

  const enhanceColourGrids = () => {
    if (!saleColours) {
      loadSaleColours();
      return;
    }
    if (!allColours.size) return;
    document.querySelectorAll('.vario-variants').forEach((wrap) => {
      const items = itemsOf(wrap);
      // Act only inside the colour grid: a size grid's values won't be
      // colour-option values. Require a majority match to be safe.
      const colourItems = items.filter((el) => {
        const holder = el.hasAttribute('data-value') ? el : el.querySelector('[data-value]');
        return allColours.has((holder.getAttribute('data-value') || '').toLowerCase());
      });
      const isColourGrid = colourItems.length > items.length / 2;
      items.forEach((el) => {
        const holder = el.hasAttribute('data-value') ? el : el.querySelector('[data-value]');
        const name = (holder.getAttribute('data-value') || '').toLowerCase();
        const onSale = isColourGrid && saleColours.has(name);
        el.classList.toggle('vario-on-sale', onSale);
        if (onSale) {
          const pct = salePct.get(name);
          el.setAttribute('data-spar-pct', pct ? '\u2212' + pct + ' %' : 'Salg');
        } else {
          el.removeAttribute('data-spar-pct');
        }
        if (isColourGrid && allColours.has(name)) addVariantLink(el, holder, name);
      });
    });
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      upgradeImages();
      ensureFilter();
      enhanceColourGrids();
    });
  };

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
